// src/api/notice/noticeApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type {
  ApiResponse,
  PageContent,
  NoticeItem,
  NoticeListQuery,
  NoticeUpsertPayload,
  NoticePinPayload,
  NoticeActivePayload,
} from "src/types/notice/noticeTypes";

const BASE = "/v1/admin/notice";

type MultipartKeys = {
  requestKey?: string; // default: "request"
  fileKey?: string; // default: "noticeFile"
};

const buildNoticeFormData = (
  payload: NoticeUpsertPayload,
  file?: File | null,
  keys: MultipartKeys = {}
) => {
  const { requestKey = "request", fileKey = "noticeFile" } = keys;
  const fd = new FormData();

  // JSON part
  fd.append(requestKey, new Blob([JSON.stringify(payload)], { type: "application/json" }));

  // file part
  if (file) fd.append(fileKey, file);

  return fd;
};

// LIST
export const getNoticeListApi = async (
  params: NoticeListQuery
): Promise<ApiResponse<PageContent<NoticeItem>>> => {
  const res = await axiosInstance.get<ApiResponse<PageContent<NoticeItem>>>(`${BASE}/list`, {
    params: {
      ...params,
      noticeTarget: params.noticeTarget || undefined,
      noticeTtl: params.noticeTtl || undefined,
      createdId: params.createdId || undefined,
      activeYn: typeof params.activeYn === "boolean" ? params.activeYn : undefined,
      activeStrDatetime: params.activeStrDatetime || undefined,
      activeEndDatetime: params.activeEndDatetime || undefined,
      order: params.order || "pinnedYn,desc:noticeOrder,asc:createdAt,desc",
    },
  });
  return res.data;
};

// CREATE (multipart)
export const createNoticeApi = async (
  payload: NoticeUpsertPayload,
  file?: File | null,
  keys?: MultipartKeys
): Promise<ApiResponse<null>> => {
  const formData = buildNoticeFormData(payload, file, keys);
  const res = await axiosInstance.post<ApiResponse<null>>(`${BASE}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// UPDATE (multipart)
export const updateNoticeApi = async (
  noticeId: number,
  payload: NoticeUpsertPayload,
  file?: File | null,
  keys?: MultipartKeys
): Promise<ApiResponse<null>> => {
  const formData = buildNoticeFormData(payload, file, keys);
  const res = await axiosInstance.put<ApiResponse<null>>(`${BASE}/${noticeId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// DELETE
export const deleteNoticeApi = async (noticeId: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`${BASE}/${noticeId}`);
  return res.data;
};

// PIN toggle (query param)
export const setNoticePinApi = async (
  noticeId: number,
  pinnedYn: boolean
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.put<ApiResponse<null>>(`${BASE}/${noticeId}/pin`, null, {
    params: { pinnedYn },
  });
  return res.data;
};

// ACTIVE toggle (query param)
export const setNoticeActiveApi = async (
  noticeId: number,
  activeYn: boolean
): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.put<ApiResponse<null>>(`${BASE}/${noticeId}/active`, null, {
    params: { activeYn },
  });
  return res.data;
};
